import { useCallback, useEffect, useState, type CSSProperties } from 'react';

import managerService, {
  type ClientSummary,
  type ManagerAgent,
} from '../../services/manager.service';
import { useRegisterWidget } from '../layout/RealtimeProvider';
import { getApiErrorMessage } from '../../lib/apiError';
import { fullName } from '../../lib/fullName';
import { colors } from '../../styles/theme';
import { useMovablePopup } from '../MovablePopup';

/**
 * Manager "Unassigned pool" — the clients with no primary agent.
 *
 * A book returns to this pool when an agent is deactivated without a successor
 * (backend admin.service.deactivate_user). Those clients are invisible in the
 * per-agent Team roster, so this surface is the only place a manager can see
 * and drain them. Hidden entirely when the pool is empty (the healthy case).
 */

const btn: CSSProperties = {
  padding: '6px 12px',
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  border: `1px solid ${colors.border}`,
  background: colors.surface,
  color: colors.ink,
};

function ReassignModal({
  clientName,
  agents,
  onCancel,
  onConfirm,
}: Readonly<{
  clientName: string;
  agents: ManagerAgent[];
  onCancel: () => void;
  onConfirm: (agentId: string, reason: string) => Promise<void>;
}>): JSX.Element {
  const popup = useMovablePopup<HTMLDivElement>();
  const [agentId, setAgentId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    setErr(null);
    try {
      await onConfirm(agentId, reason.trim());
      // Parent unmounts this modal on success.
    } catch (e) {
      setErr(getApiErrorMessage(e, 'Reassignment failed.'));
      setSubmitting(false);
    }
  };

  const canSubmit = agentId !== '' && reason.trim().length >= 5 && !submitting;

  return (
    <dialog
      open
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
        maxWidth: 'none',
        maxHeight: 'none',
        // A <dialog> is only as large as its content unless it is given a size.
        width: '100%',
        height: '100%',
        margin: 0,
        border: 'none',
      }}
    >
      <div
        ref={popup.panelRef}
        style={{
          ...popup.panelStyle,
          background: colors.surface,
          borderRadius: 14,
          padding: 22,
          width: 'min(460px, 100%)',
          border: `1px solid ${colors.border}`,
        }}
      >
        {popup.bar}
        <h3 style={{ fontSize: 16, fontWeight: 700, color: colors.ink, margin: 0 }}>
          Reassign {clientName}
        </h3>
        <p style={{ fontSize: 13, color: colors.muted, margin: '8px 0 14px' }}>
          Pick the agent who will own this client. Any active onboarding cases move with them.
        </p>

        {err && (
          <div
            role="alert"
            style={{
              background: colors.dangerBg,
              color: colors.danger,
              border: `1px solid ${colors.dangerBorder}`,
              borderRadius: 8,
              padding: '8px 10px',
              fontSize: 12,
              marginBottom: 12,
            }}
          >
            {err}
          </div>
        )}

        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: colors.inkSoft }}>
          Agent
          {/* */}
          <select
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
            style={{
              width: '100%',
              marginTop: 4,
              padding: 8,
              borderRadius: 8,
              border: `1px solid ${colors.border}`,
              fontSize: 13,
            }}
          >
            <option value="">Select an agent…</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {fullName({ first_name: a.first_name, last_name: a.last_name })} — {a.email}
              </option>
            ))}
          </select>
        </label>

        <label
          style={{ display: 'block', fontSize: 12, fontWeight: 600, color: colors.inkSoft, marginTop: 12 }}
        >
          Reason (audited, min 5 chars)
          {/* */}
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            style={{
              width: '100%',
              marginTop: 4,
              padding: 8,
              borderRadius: 8,
              border: `1px solid ${colors.border}`,
              fontSize: 13,
              resize: 'vertical',
            }}
          />
        </label>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
          <button type="button" onClick={onCancel} disabled={submitting} style={btn}>
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            style={{
              ...btn,
              background: canSubmit ? colors.navy : colors.canvas,
              color: canSubmit ? '#fff' : colors.mutedSoft,
              border: 'none',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            {submitting ? 'Working…' : 'Reassign'}
          </button>
        </div>
      </div>
    </dialog>
  );
}

export default function UnassignedPool({
  agents,
  onReassigned,
}: Readonly<{ agents: ManagerAgent[]; onReassigned: () => void }>): JSX.Element | null {
  const [pool, setPool] = useState<ClientSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [reassigning, setReassigning] = useState<ClientSummary | null>(null);

  const loadPool = useCallback((): void => {
    managerService
      .listUnassignedClients()
      .then((items) => {
        setPool(items);
        setError(null);
      })
      .catch((e: unknown) => {
        setError(getApiErrorMessage(e, 'Could not load the unassigned pool.'));
      });
  }, []);

  useEffect(() => {
    loadPool();
  }, [loadPool]);

  // Live refresh: a reassignment made on another screen (Dashboard, or by
  // another manager) publishes CASE_REASSIGNED → staff channel with
  // 'agent-distribution' among its trigger_widgets. Registering the refetch
  // under that key drains/repopulates this pool without a reload — matching
  // the Dashboard's "Unassigned · needs reassignment" section. Registered
  // under a single key so one event triggers exactly one refetch.
  useRegisterWidget('agent-distribution', loadPool);

  const confirmReassign = async (agentId: string, reason: string): Promise<void> => {
    if (!reassigning) return;
    const clientId = reassigning.id;
    await managerService.reassignClientAgent(clientId, { new_agent_id: agentId, reason });
    setPool((prev) => (prev ?? []).filter((c) => c.id !== clientId));
    setReassigning(null);
    onReassigned();
  };

  // Hide entirely when the pool is empty (the common, healthy case).
  if (error === null && pool !== null && pool.length === 0) return null;

  const count = pool?.length ?? 0;

  return (
    <section
      style={{
        background: colors.surface,
        border: `1px solid ${colors.warning}`,
        borderRadius: 14,
        marginBottom: 16,
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '12px 16px',
          textAlign: 'left',
          background: colors.warningBg,
        }}
      >
        <div>
          <p style={{ fontSize: 14, fontWeight: 700, color: colors.ink, margin: 0 }}>
            Unassigned pool
          </p>
          <p style={{ fontSize: 12, color: colors.muted, margin: '2px 0 0' }}>
            Clients with no agent — reassign to drain.
          </p>
        </div>
        <span
          style={{
            background: colors.warning,
            color: '#fff',
            fontSize: 12,
            fontWeight: 700,
            padding: '2px 10px',
            borderRadius: 999,
          }}
        >
          {count}
        </span>
      </button>

      {open && (
        <div style={{ padding: 12 }}>
          {error && (
            <p role="alert" style={{ fontSize: 13, color: colors.danger }}>
              {error}
            </p>
          )}
          {pool === null && !error && (
            <p style={{ fontSize: 13, color: colors.muted }}>Loading…</p>
          )}
          {pool?.map((c) => (
            <div
              key={c.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '8px 4px',
                borderTop: `1px solid ${colors.borderSoft}`,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: colors.ink, margin: 0 }}>
                  {fullName({ first_name: c.first_name, last_name: c.last_name })}
                </p>
                <span style={{ fontSize: 12, color: colors.muted, fontFamily: 'monospace' }}>
                  {c.email}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setReassigning(c)}
                style={{ ...btn, background: colors.navy, color: '#fff', border: 'none' }}
              >
                Reassign
              </button>
            </div>
          ))}
        </div>
      )}

      {reassigning && (
        <ReassignModal
          clientName={fullName({
            first_name: reassigning.first_name,
            last_name: reassigning.last_name,
          })}
          agents={agents}
          onCancel={() => setReassigning(null)}
          onConfirm={confirmReassign}
        />
      )}
    </section>
  );
}
